<template>
  <v-card class="py-5">
    <v-card-title class="justify-center text-h4 pb-10 primary--text">ตั้งค่าข้อความหลังประเมิน</v-card-title>
    <v-card-text class="popup-background">
      <v-row justify="center">
        <v-col cols="12" md="6">
          <v-text-field
            class="pt-5"
            v-model="draft.service_rating_thank_you_title"
            label="หัวข้อ"
            placeholder="ขอบคุณค่ะ"
            counter="120"
            maxlength="120"
            outlined
            dense
            background-color="white"
          />
          <v-textarea
            v-model="draft.service_rating_thank_you_content"
            label="เนื้อหา"
            placeholder="หากต้องการแจ้งข้อเสนอแนะหรือร้องเรียน สามารถติดต่อเราได้ที่..."
            counter="500"
            maxlength="500"
            outlined
            dense
            rows="4"
            background-color="white"
          />
          <div class="mb-5">
            <div class="mb-2">รูปภาพ / QR Code (ไม่บังคับ)</div>
            <uploadImage
              ref="thankYouImageUpload"
              directory="service_rating_thank_you"
              :limit="5"
              :max-size="800"
              cleanup-on-destroy
              @uploading="onUploading"
              :src="draft.service_rating_thank_you_image_url"
              @onChangeUrl="draft.service_rating_thank_you_image_url = $event"
              buttons
            >
              <template #default="{ open, remove, uploading }">
                <img
                  v-if="draft.service_rating_thank_you_image_url"
                  :src="draft.service_rating_thank_you_image_url"
                  alt="รูปภาพ/QR Code สำหรับข้อความหลังประเมิน"
                  width="150"
                  style="height: auto"
                />
                <div class="mt-2">
                  <v-btn small color="primary" :disabled="uploading" @click="open">
                    <v-icon left small>mdi-upload</v-icon>{{ draft.service_rating_thank_you_image_url ? 'เปลี่ยนรูป' : 'เพิ่มรูป' }}
                  </v-btn>
                  <v-btn
                    v-if="draft.service_rating_thank_you_image_url"
                    small
                    outlined
                    color="error"
                    class="ml-2"
                    :disabled="uploading"
                    @click="remove"
                  >
                    <v-icon left small>mdi-delete</v-icon>ลบ
                  </v-btn>
                </div>
                <div class="mt-2 grey--text text--darken-1" style="font-size: 0.9rem">
                  JPG, PNG ไม่เกิน 5MB เช่น QR Code, LINE, ช่องทางร้องเรียน
                </div>
              </template>
            </uploadImage>
          </div>
          <v-text-field
            :value="`${draft.service_rating_display_seconds || 60} วินาที`"
            label="ระยะเวลาแสดงหน้าขอบคุณ"
            outlined
            dense
            readonly
            hint="ค่าคงที่สำหรับเวอร์ชันนี้"
            persistent-hint
            background-color="grey lighten-4"
          />
        </v-col>

        <v-col cols="12" md="6">
          <div class="mb-2 grey--text text--darken-1">ตัวอย่างที่ลูกค้าจะเห็นหลังให้คะแนน</div>
          <v-card outlined class="pa-8 text-center" style="background: #f5f3ff; min-height: 320px">
            <img
              v-if="draft.service_rating_thank_you_image_url"
              :src="draft.service_rating_thank_you_image_url"
              alt="ตัวอย่างรูปภาพ/QR Code ที่ลูกค้าจะเห็น"
              width="140"
              style="height: auto"
              class="mb-4"
              data-test="service-rating-preview-image"
            />
            <div class="text-h5 primary--text mb-3" data-test="service-rating-preview-title">
              {{ draft.service_rating_thank_you_title || 'ขอบคุณที่ใช้บริการ' }}
            </div>
            <div
              v-if="draft.service_rating_thank_you_content"
              style="white-space: pre-line"
              data-test="service-rating-preview-content"
            >
              {{ draft.service_rating_thank_you_content }}
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="py-5">
      <v-spacer />
      <v-btn outlined class="px-5 mr-10" color="grey" :disabled="uploading" @click="cancel()">ยกเลิก</v-btn>
      <v-btn class="px-10 mr-15" color="primary" :disabled="uploading" @click="apply()">นำไปใช้</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import uploadImage from '@/components/uploadImage.vue';

export default {
  components: {
    uploadImage,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      // A local copy, not a live reference to the parent's setting object:
      // edits here (including an upload) must not reach the parent/branch
      // settings until "นำไปใช้" (Apply) is pressed.
      draft: this.draftFrom(this.item),
      uploading: false,
    };
  },
  methods: {
    draftFrom(item) {
      const source = item || {};
      return {
        service_rating_thank_you_title: source.service_rating_thank_you_title || '',
        service_rating_thank_you_content: source.service_rating_thank_you_content || '',
        service_rating_thank_you_image_url: source.service_rating_thank_you_image_url || '',
        service_rating_display_seconds: source.service_rating_display_seconds || 60,
      };
    },
    onUploading(value) {
      this.uploading = value;
      this.$emit('onUploading', value);
    },
    // Discard: do nothing special - an uploaded-but-unapplied image is cleaned
    // up automatically by uploadImage's own beforeDestroy() once this dialog
    // (v-if) is torn down, exactly like never confirming it in the first place.
    cancel() {
      this.$emit('onClose');
    },
    // Keep: tell the upload component this session's image (if any) is being
    // kept, not discarded, BEFORE the dialog closes and destroys it - then
    // hand the draft to the parent to merge into the real branch setting.
    apply() {
      if (this.$refs.thankYouImageUpload) this.$refs.thankYouImageUpload.keepUpload();
      this.$emit('onApply', { ...this.draft });
      this.$emit('onClose');
    },
  },
};
</script>
